import React, { useRef, useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	Alert,
	Button,
	Typography,
	Tabs,
	Tab,
	TextField,
	InputAdornment,
} from '@mui/material';
import {
	FileUploadRounded,
	CheckCircleRounded,
	InsertDriveFileRounded,
	LinkRounded,
	AddLinkRounded,
} from '@mui/icons-material';
import { apiErrorMessage } from '../../../api/catalog';
import {
	DropzoneArea,
	DropzoneIconWrapper,
	DropzoneHint,
	FileTypeChip,
	ModeSwitchContainer,
	UrlFieldsContainer,
	ModalActions,
} from './styles';

export type RecursoInput =
	| { tipo: 'archivo'; file: File }
	| { tipo: 'enlace'; url: string; nombre: string };

interface SubirRecursoModalProps {
	open: boolean;
	materiaNombre: string;
	title?: string;
	onClose: () => void;
	onConfirmar: (input: RecursoInput) => Promise<void>;
}

type ModoSubida = 'archivo' | 'enlace';

const isValidUrl = (url: string): boolean => {
	try {
		const parsed = new URL(url);
		return ['http:', 'https:'].includes(parsed.protocol);
	} catch {
		return false;
	}
};

const SubirRecursoModal: React.FC<SubirRecursoModalProps> = ({
	open,
	materiaNombre,
	title = 'Subir recurso',
	onClose,
	onConfirmar,
}) => {
	const [modo, setModo] = useState<ModoSubida>('archivo');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [urlNombre, setUrlNombre] = useState('');
	const [urlValue, setUrlValue] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Reset state when modal opens/closes
	useEffect(() => {
		if (!open) handleReset();
	}, [open]);

	const getExtension = (file: File) =>
		file.name.split('.').pop()?.toUpperCase() ?? 'FILE';

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	const handleDropzoneClick = () => fileInputRef.current?.click();

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) setSelectedFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = () => setIsDragging(false);

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files?.[0];
		if (file) setSelectedFile(file);
	};

	const handleConfirmar = async () => {
		if (saving) return;
		setSaving(true);
		setError(null);
		try {
			if (modo === 'archivo' && selectedFile) {
				await onConfirmar({ tipo: 'archivo', file: selectedFile });
			} else if (modo === 'enlace' && urlNombre.trim() && isValidUrl(urlValue)) {
				await onConfirmar({ tipo: 'enlace', url: urlValue.trim(), nombre: urlNombre.trim() });
			} else {
				setSaving(false);
				return;
			}
			handleReset();
		} catch (err) {
			setError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (saving) return;
		handleReset();
		onClose();
	};

	const handleReset = () => {
		setModo('archivo');
		setSelectedFile(null);
		setIsDragging(false);
		setUrlNombre('');
		setUrlValue('');
		setError(null);
		setSaving(false);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const isConfirmDisabled =
		modo === 'archivo' ? !selectedFile : !urlNombre.trim() || !isValidUrl(urlValue);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>{title}</DialogTitle>

			<DialogContent>
				<DialogContentText>
					Subiendo a: <strong>{materiaNombre}</strong>. El recurso quedará en revisión hasta
					ser aprobado.
				</DialogContentText>

				<ModeSwitchContainer>
					<Tabs
						value={modo}
						onChange={(_, val: ModoSubida) => setModo(val)}
						textColor='primary'
						indicatorColor='primary'
					>
						<Tab
							value='archivo'
							label='Subir archivo'
							icon={<FileUploadRounded fontSize='small' />}
							iconPosition='start'
						/>
						<Tab
							value='enlace'
							label='Enlace URL'
							icon={<LinkRounded fontSize='small' />}
							iconPosition='start'
						/>
					</Tabs>
				</ModeSwitchContainer>

				{modo === 'archivo' && (
					<>
						<input ref={fileInputRef} type='file' hidden onChange={handleInputChange} />
						<DropzoneArea
							isDragging={isDragging}
							hasFile={!!selectedFile}
							onClick={handleDropzoneClick}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
						>
							<DropzoneIconWrapper hasFile={!!selectedFile}>
								{selectedFile ? (
									<CheckCircleRounded fontSize='large' />
								) : (
									<FileUploadRounded fontSize='large' />
								)}
							</DropzoneIconWrapper>

							{selectedFile ? (
								<>
									<InsertDriveFileRounded color='action' fontSize='small' />
									<Typography variant='body2' fontWeight={600} noWrap>
										{selectedFile.name}
									</Typography>
									<FileTypeChip>
										{getExtension(selectedFile)} · {formatFileSize(selectedFile.size)}
									</FileTypeChip>
									<Typography variant='caption' color='text.secondary'>
										Haz clic para cambiar el archivo
									</Typography>
								</>
							) : (
								<>
									<Typography variant='body2' fontWeight={500} color='text.primary'>
										Arrastra tu archivo aquí o haz clic para seleccionar
									</Typography>
									<DropzoneHint>PDF, Word, PowerPoint, Excel, Video, Imagen y más</DropzoneHint>
								</>
							)}
						</DropzoneArea>
					</>
				)}

				{modo === 'enlace' && (
					<UrlFieldsContainer>
						<TextField
							fullWidth
							label='Nombre del recurso *'
							placeholder='Ej. Apuntes de Física — Unidad 3'
							value={urlNombre}
							onChange={(e) => setUrlNombre(e.target.value)}
							autoFocus
						/>
						<TextField
							fullWidth
							label='URL del enlace *'
							placeholder='https://...'
							value={urlValue}
							onChange={(e) => setUrlValue(e.target.value)}
							type='url'
							error={urlValue.length > 0 && !isValidUrl(urlValue)}
							helperText={
								urlValue.length > 0 && !isValidUrl(urlValue)
									? 'Ingresa una URL válida (debe comenzar con http:// o https://)'
									: undefined
							}
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<LinkRounded />
									</InputAdornment>
								),
							}}
						/>
					</UrlFieldsContainer>
				)}
				{error && <Alert severity='error'>{error}</Alert>}
			</DialogContent>

			<ModalActions>
				<Button variant='outlined' onClick={handleClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					variant='contained'
					onClick={handleConfirmar}
					disabled={isConfirmDisabled || saving}
					startIcon={modo === 'archivo' ? <FileUploadRounded /> : <AddLinkRounded />}
				>
					{saving ? 'Guardando...' : modo === 'archivo' ? 'Subir recurso' : 'Añadir enlace'}
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default SubirRecursoModal;
